import { Component } from '@mycolorway/tao';
import $ from 'jquery';
import { closest, matches } from 'next/javascripts/modules/dom';
import resourceSubject from 'next/javascripts/reactivity/resource-subject';
import eventListenerMixin from 'next/javascripts/mixins/event-listener-mixin';
import { enqueueTask } from 'next/javascripts/modules/queued-task';
import { observe, unobserve, isIntersecting } from './intersection-observer';
import 'next/stylesheets/components/grid/todo-row.scss';

Component('tr-grid-todo-row', {
  mixins: [eventListenerMixin],

  properties: {
    guid: {
      type: String,
      observer: 'guidChanged',
    },

    creationUrl: String,
    editUrl: String,
    completionStatusUrl: String,
    detailUrl: String,
    deleteUrl: String,

    completed: {
      type: Boolean,
      default: false,
      observer: 'completedChanged',
    },

    running: {
      type: Boolean,
      default: false,
      observer: 'runningChanged',
    },

    hiddenColumnKeys: {
      type: Object,
      default: [],
      observer: 'hiddenColumnKeysChanged',
    },

    editing: {
      type: Boolean,
      default: false,
    },

    selected: {
      type: Boolean,
      default: false,
    },

    sortable: {
      type: Boolean,
      default: false,
    },

    intersectionRootSelector: {
      type: String,
      default: 'tr-todo-grid .grid-vertical-scrollable',
    },

    saving: {
      type: Boolean,
      default: false,
      observer: 'savingChanged',
    },

    expandable: {
      type: Boolean,
      default: false,
    },

    expanded: {
      type: Boolean,
      default: false,
      observer: 'expandedChanged',
    },
  },

  connected() {
    this.pendingChanges = {};
    this.rowContent = this.querySelector('.row-content');

    this.listenTo(this, {
      click: this.handleClick.bind(this),
      'tao:connected': () => {
        this.namespacedTrigger('afterConnected', { bubbles: true });
      },
      'tao:cellEditingChange': this.handleCellEditingChange.bind(this),
      'tao:cellValueChange': this.handleCellValueChange.bind(this),
      'tao:openInTab': this.handleOpenDetailsInTab.bind(this),
      'tao:openInCurrent': (e) => {
        e.stopPropagation();
        this.namespacedTrigger('open', {
          detail: {
            detailUrl: this.detailUrl,
          },
          bubbles: true,
        });
      },
      'tr-grid-todo-title-cell:escape': () => {
        if (!this.guid) {
          this.namespacedTrigger('beforeDisconnected', { bubbles: true });
          this.parentNode.removeChild(this);
        }
      },
      'tr-grid-todo-title-cell:backspace': this.handleTodoTitleBackspace.bind(this),
      'tr-grid-todo-title-cell:completedChange': this.handleCompletionStatusChange.bind(this),
    });

    this.listenTo(document, {
      keydown: (e) => {
        if (window.tower.metaKey(e)) this.rowContent.classList.add('cmd');
      },
      keyup: () => this.rowContent.classList.remove('cmd'),
    });

    if (!this.guid) {
      document.addEventListener('mousedown', this.handleMouseDown.bind(this));
    }

    this.initSubGroup();
    this.layout();
  },

  ready() {
    this.titleCell = this.querySelector('tr-grid-todo-title-cell');

    observe(this, 'tr-grid-todo-row', this.handleIntersection.bind(this), {
      root: document.querySelector(this.intersectionRootSelector),
      rootMargin: '700px 0px 700px 0px',
    });

    this.gridSubscription = resourceSubject('tr-grid').subscribe({
      next: this.handleGridSubscriptionEvent.bind(this),
    });

    this.todoSubscription = resourceSubject('todo').subscribe({
      next: this.syncCellValues.bind(this),
    });
  },

  disconnected() {
    unobserve(this, 'tr-grid-todo-row');
    this.stopListening();
    if (this.gridSubscription) this.gridSubscription.unsubscribe();
    if (this.todoSubscription) this.todoSubscription.unsubscribe();
    if (this.sortable && !closest(this, '.sortable-chosen, .sortable-drag')) {
      this.subGroup.destroySortable();
    }
  },

  initSubGroup() {
    this.parentSubGroup = closest(this, 'tr-grid-subtodo-group');
    this.subGroup = this.querySelector('tr-grid-subtodo-group');
    this.subGroup.sortable = this.sortable;

    this.listenTo(this, 'tr-grid-todo-title-cell:expandRow', (e) => {
      e.stopPropagation();
      if (!this.expandable) return;

      this.expanded = !this.expanded;
      this.subGroup.expanded = this.expanded;
      if (this.expanded) {
        this.subGroup.loadOnce();
      }

      this.subGroup.querySelectorAll('.grid-cell')
        .forEach(cell => cell.classList.toggle('skip-summary', !this.expanded));

      this.namespacedTrigger('expandRow', {
        detail: this.expanded, bubbles: true,
      });
    });
  },

  handleIntersection(entry) {
    if (this.parentSubGroup) return;

    if (isIntersecting(entry)) {
      this.restoreOriginalCells();
    } else {
      this.replaceCellsWithPlaceholder();
    }
  },

  handleOpenDetailsInTab(e) {
    e.stopPropagation();
    if (this.detailUrl) {
      window.open(this.detailUrl);
    }
  },

  handleMouseDown(event) {
    const { target } = event;
    const title = this.querySelector('tr-grid-todo-title-cell textarea').value;
    const mouseDownInRow = this.contains(target) || closest(target, '.grid-cell-editor-menu', this);
    const clickNewTodoBtn = closest(target, '.new-todo-button')
      || closest(target, '.todolist-header .link-new-todo');
    if (!mouseDownInRow
      && !title
      && !closest(target, 'tr-todo-page')
      && !clickNewTodoBtn) {
      this.namespacedTrigger('beforeDisconnected', { bubbles: true });
      this.remove();
    }
  },

  replaceCellsWithPlaceholder() {
    enqueueTask(() => {
      if (this.isPlaceholder()) return;

      this.classList.add('grid-row-placeholder');

      const title = this.rowContent.querySelector('tr-grid-todo-title-cell .todo-rest');
      let placeholder = this.rowContent.querySelector('.grid-cell-placeholder');

      if (!placeholder) {
        placeholder = this.rowContent.insertAdjacentHTML('afterbegin', `
          <div class="grid-cell grid-cell-placeholder">
            <div class="grid-row-placeholder-inner">
              ${title ? title.textContent : ''}
            </div>
          </div>
        `);
      }

      this.stickyCellStyle = null;
      this.stickyCellPlaceholderStyle = null;
    });
  },

  restoreOriginalCells() {
    enqueueTask(() => {
      if (!this.isPlaceholder()) return;
      this.classList.remove('grid-row-placeholder');

      const placeholder = this.querySelector('.grid-cell-placeholder');
      if (placeholder) placeholder.parentNode.removeChild(placeholder);

      if (this.queuedGridEvents) {
        Object.keys(this.queuedGridEvents).forEach(
          event => this.consumeGridSubscriptionEvent(event, this.queuedGridEvents[event]),
        );
        this.queuedGridEvents = null;
      }
    });
  },

  isPlaceholder() {
    return this.classList.contains('grid-row-placeholder');
  },

  handleGridSubscriptionEvent(event, payload) {
    if (this.isPlaceholder()) {
      this.queuedGridEvents = this.queuedGridEvents || {};
      this.queuedGridEvents[event] = payload;
    } else {
      this.consumeGridSubscriptionEvent(event, payload);
    }
  },

  consumeGridSubscriptionEvent(event, payload) {
    switch (event) {
      case 'hiddenColumnKeysChanged':
        this.hiddenColumnKeys = payload.hiddenColumnKeys;
        break;
      case 'minimalChanged':
        this.toggleCellsMinimalStatus(payload.grid.minimal);
        break;
      case 'columnResized':
        this.updateColumnWidth({ key: payload.syncKey, width: payload.width });
        break;
      default:
        break;
    }
  },

  syncCellValues(event, payload) {
    if (!payload.guid || payload.guid !== this.guid
      || payload.isTouched || payload.from === 'grid') return;

    if (event === 'update' || event === 'update:pusher') {
      if (payload.rowData) {
        this.syncCellValuesFromDetail(payload.rowData);
      } else {
        this.refreshCellValues();
      }
    } else if (event === 'destroy' || event === 'delete:pusher') {
      this.namespacedTrigger('beforeDisconnected', { bubbles: true });
      this.parentNode.removeChild(this);
    } else if (event === 'move') {
      const isBatchTask = !!payload.isBatchTask;
      const todolistGuid = payload.todolistGuid || payload.rowData.todolistGuid;
      if (payload.isSubTodo || payload.projectGuid !== payload.originalProjectGuid) {
        this.namespacedTrigger('beforeDisconnected', { bubbles: true });
        this.parentNode.removeChild(this);
      } else if (!payload.isSubTodo) {
        this.namespacedTrigger('move', {
          bubbles: true,
          detail: { todolistGuid, isBatchTask },
        });
      }
    }
  },

  syncCellValuesFromDetail(data) {
    const attributeNames = Object.keys(data);
    if (attributeNames.length > 4) {
      this.refreshCellValues();
      return;
    }

    let cell;

    if (attributeNames.indexOf('assignee') !== -1) {
      const assigneeGuid = data.assignee
        ? JSON.parse(data.assignee).value : 'no-assignee';
      cell = this.querySelector('.grid-cell[key="assignee_guid"]');
      if (cell) cell.setValue(assigneeGuid);
    } else if (attributeNames.indexOf('priority') !== -1) {
      const priority = JSON.parse(data.priority).value;
      cell = this.querySelector('.grid-cell[key="priority"]');
      if (cell) cell.setValue(priority);
    } else if (attributeNames.indexOf('start_date') !== -1) {
      cell = this.querySelector('.grid-cell[key="due_date"]');
      if (cell) {
        cell.setValue([data.start_date, data.due_date].filter(v => !!v).join(','));
      }
    } else if (attributeNames.indexOf('running') !== -1) {
      this.running = data.running;
    } else {
      this.refreshCellValues();
    }
  },

  refreshCellValues() {
    this.namespacedTrigger('refresh', { bubbles: true });
  },

  toggleCellsMinimalStatus(minimal) {
    Array.from(this.rowContent.children).forEach((cell) => {
      if (matches(cell, '.grid-cell') && cell.toggleMinimalStatus) {
        cell.toggleMinimalStatus(minimal);
      }
    });
  },

  handleClick(event) {
    if (!this.guid) return;
    if (closest(event.target, 'tr-grid-todo-row') !== this) {
      return;
    }

    const preventClick = Array.from(this.rowContent.querySelectorAll('.grid-cell'))
      .some(cell => cell.preventClickEvent(event));
    if (!preventClick) {
      event.stopPropagation();
      this.namespacedTrigger('click', {
        detail: { originalEvent: event },
        bubbles: true,
      });
    }
  },

  handleCellEditingChange(event) {
    event.stopPropagation();
    this.editing = event.detail
      || Array.from(this.querySelectorAll('.grid-cell')).some(cell => cell.editing);
  },

  handleCellValueChange(event) {
    event.stopPropagation();
    const { target } = event;
    const { value } = event.detail;

    if (this.guid) {
      if (target.key !== 'content') {
        this.updateTodo({ [target.key]: value });
      } else if (value) {
        target.disabled = true;
        this.updateTodo({ [target.key]: value });
      } else {
        target.editing = false;
      }
    } else if (target.key === 'content' && value) {
      target.disabled = true;
      this.createNewTodo(value);
    } else if (this.todoCreationTask) {
      this.pendingChanges[target.key] = value;
    }
  },

  createNewTodo(content) {
    const data = { content };

    Array.from(this.rowContent.children).forEach((child) => {
      if (matches(child, '.grid-cell')) {
        data[child.key] = child.value;
      }
    });

    this.saving = true;

    this.todoCreationTask = $.ajax({
      url: this.creationUrl,
      method: 'post',
      data: {
        todos_creation: data,
        prepend: this.previousElementSibling ? '' : true,
        prev_todo_guid: this.previousElementSibling ? this.previousElementSibling.guid : '',
        parent_guid: this.parentSubGroup ? this.parentSubGroup.parentGuid : null,
        tao_id: this.taoId,
      },
      dataType: 'script',
    }).then(() => {
      this.todoCreationTask = null;
      this.saving = false;
    });
  },

  updateTodo(params = {}) {
    this.saving = true;

    $.ajax({
      url: this.editUrl,
      method: 'patch',
      contentType: 'application/json;charset-utf-8',
      data: JSON.stringify({
        todos_management: params,
      }),
      dataType: 'script',
    }).then(() => { this.saving = false; });
  },

  deleteTodo() {
    this.style.display = 'none';

    $.ajax({
      url: this.deleteUrl,
      method: 'post',
      dataType: 'script',
    }).then(() => {
      this.namespacedTrigger('beforeDisconnected', { bubbles: true });
      this.parentNode.removeChild(this);
    });
  },

  guidChanged(newGuid, oldGuid) {
    if (!oldGuid && newGuid) {
      document.removeEventListener('mousedown', this.handleMouseDown.bind(this));
      if (Object.keys(this.pendingChanges).length > 0) {
        this.updateTodo(this.pendingChanges);
        this.pendingChanges = {};
      }
    }
  },

  completedChanged(completed) {
    const titleCell = this.querySelector('tr-grid-todo-title-cell');

    if (titleCell && titleCell.completed !== completed) {
      titleCell.completed = completed;
    }

    if (this.expandable) {
      this.expanded = false;
    }

    this.namespacedTrigger('completed', {
      detail: { completed },
      bubbles: true,
    });
  },

  runningChanged(running) {
    const titleCell = this.querySelector('tr-grid-todo-title-cell');

    if (titleCell && titleCell.running !== running) {
      titleCell.running = running;
    }

    this.namespacedTrigger('running', {
      detail: { running },
      bubbles: true,
    });
  },

  handleTodoTitleBackspace(e) {
    e.stopPropagation();

    if (!this.guid) {
      this.namespacedTrigger('beforeDisconnected', { bubbles: true });
      this.parentNode.removeChild(this);
    } else {
      this.deleteTodo();
    }
  },

  handleCompletionStatusChange(e) {
    e.stopPropagation();

    if (this.completionStatusUrl && !this.completionRequest) {
      this.completionRequest = $.ajax({
        url: this.completionStatusUrl,
        method: 'post',
        dataType: 'script',
      }).then(() => {
        this.completionRequest = null;
      });
    }
  },

  hiddenColumnKeysChanged(hiddenColumnKeys) {
    Array.from(this.rowContent.children).forEach((cell) => {
      if (!matches(cell, '.grid-cell')) return;

      const hidden = hiddenColumnKeys.indexOf(cell.key) !== -1;
      cell.classList.toggle('hidden', hidden);
    });
  },

  savingChanged(isSaving) {
    this.titleCell.disabled = isSaving;
  },

  updateStickyCellWidth(width) {
    if (this.isPlaceholder()) return;

    this.stickyCellStyle = this.stickyCellStyle
      || this.querySelector('.grid-sticky-cell').style;
    this.stickyCellPlaceholderStyle = this.stickyCellPlaceholderStyle
      || this.querySelector('.grid-sticky-cell-placeholder').style;

    this.stickyCellStyle.width = `${width}px`;
    this.stickyCellPlaceholderStyle.width = `${width}px`;
  },

  updateColumnWidth({ key, width }) {
    if (this.isPlaceholder()) return;
    if (key === 'stickyCell') {
      this.updateStickyCellWidth(width);
    } else {
      this.querySelector(`[key="${key}"]`).style.width = `${width}px`;
    }
  },

  expandedChanged(expanded) {
    if (this.expandable) {
      this.subGroup.expanded = expanded;
    }
  },

  layout() {
    const titleCell = this.querySelector('.grid-sticky-cell > .grid-cell');
    if (this.parentSubGroup) {
      const left = this.parentSubGroup.calcIndentSize() + 4; // plus 4 for center;
      titleCell.style.setProperty('padding-left', `${left}px`, 'important');
    } else {
      titleCell.style.paddingLeft = null;
    }
  },

  selectable() {
    if (this.parentSubGroup) {
      return this.parentSubGroup.expanded;
    }
    return true;
  },

  getSubGroup() {
    return this.subGroup;
  },

  getParentSubGroup() {
    return this.parentSubGroup;
  },
});
